import { useState, useMemo, useCallback, useEffect } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import EmployeeService from "../../../../services/employee_service";
import RoleService from "../../../../services/role_service";
import { GLOBAL_STATUS } from "../../../../constants/common";

/**
 * useEmployees
 * Hook de lógica para o módulo de Colaboradores.
 */
export const useEmployees = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "fullName",
        orderDirection: "asc"
    }), []);

    // Paginação dos Colaboradores (ignora 'owner')
    const { data: employees, loading, error, refresh } = usePagination(
        `users`,
        idTenant,
        paginationOptions,
        [{ field: "role", operator: "!=", value: "owner" }]
    );

    const [roles, setRoles] = useState([]);

    // Busca os Cargos (Roles) disponíveis para o formulário
    const fetchRoles = useCallback(async () => {
        if (!idTenant) return;
        try {
            // Utilizando o RoleService para manter a consistência de permissões e listagem do tenant
            const list = await RoleService.getRoles(idTenant);
            setRoles(list || []);
        } catch (error) {
            console.error("Erro ao buscar cargos disponíveis:", error);
            // Non-blocking err...
        }
    }, [idTenant]);

    // Executa na montagem
    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    /**
     * Salvar um Colaborador
     */
    const saveEmployee = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            const savedEmployee = await EmployeeService.save(formData, idTenant, user, employees, roles);
            toast.success(formData.id ? "Colaborador atualizado!" : "Novo colaborador criado com sucesso!");
            refresh();
            return savedEmployee;
        } catch (err) {
            console.error("Erro ao salvar colaborador:", err);
            toast.error(err?.message || "Erro ao salvar colaborador.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, employees, roles, refresh]);

    // O EmployeeService não tem delete físico nativo ainda para Auth Users (geralmente só inativamos no status).
    // Mas a UI vai oferecer uma inativação rápida ou podemos adicionar o delete.
    const changeEmployeeStatus = useCallback(async (employeeId, currentStatus) => {
        if (!idTenant) return;

        const newStatus = currentStatus === GLOBAL_STATUS.ACTIVE ? GLOBAL_STATUS.INACTIVE : GLOBAL_STATUS.ACTIVE;
        const actionLabel = newStatus === GLOBAL_STATUS.ACTIVE ? 'Ativar' : 'Inativar';

        const confirmed = await ConfirmDialog({
            title: `${actionLabel} Colaborador?`,
            text: `Tem certeza que deseja ${actionLabel.toLowerCase()} o acesso deste colaborador?`,
            confirmButtonText: "Sim, confirmar",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            const employeeData = employees.find(e => e.id === employeeId);
            if (employeeData) {
                await EmployeeService.save({ ...employeeData, status: newStatus }, idTenant, user, employees);
                toast.success(`Colaborador ${newStatus === GLOBAL_STATUS.ACTIVE ? 'Ativado' : 'Inativado'}!`);
                refresh();
            }
            return true;
        } catch (err) {
            console.error("Erro ao inativar colaborador:", err);
            toast.error("Não foi possível mudar o status.");
            return false;
        }
    }, [idTenant, user, employees, refresh]);

    return {
        employees,
        roles,
        loading,
        error,
        isSaving,
        actions: {
            saveEmployee,
            changeEmployeeStatus,
            refresh
        }
    };
};
